import { FC, useState } from "react"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import BBSwitch from "../../form/Switch"
import BrandAutocomplete from "../../brand-autocomplete"
import { Brand } from "../../../../types"

interface Props {
  backLabel: string
  namePlaceholder: string
  name: string | null
  pageTitle: string
  isCreate: boolean
  isValid: boolean
  dirty: boolean
  isDefault: boolean
  isEnabled?: boolean
  brands: Brand[]
  brandId: string
  onNameChange: (val: string) => void
  onDefaultChange: (v: boolean) => void
  onEnabledChange?: (v: boolean) => void
  onBrandChange: (val: string) => void
  onDelete: () => void
  onCancel: () => void
  onSubmit: () => void
  onBack: () => void
}

const ConfigEditHeader: FC<Props> = ({
  backLabel,
  namePlaceholder,
  name,
  pageTitle,
  isCreate,
  isValid,
  dirty,
  isDefault,
  isEnabled,
  brands,
  brandId,
  onNameChange,
  onDefaultChange,
  onEnabledChange,
  onBrandChange,
  onDelete,
  onCancel,
  onSubmit,
  onBack
}) => {
  const [isEditingName, setIsEditingName] = useState(false)

  return (
    <div className="bg-white px-6 py-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
      >
        <ArrowLeft size={14} />
        {backLabel}
      </button>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex items-center gap-3">
          {isCreate ? (
            <>
              {isEditingName ? (
                <input
                  autoFocus
                  value={name ?? ""}
                  onChange={e => onNameChange(e.target.value)}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={e => e.key === "Enter" && setIsEditingName(false)}
                  placeholder={namePlaceholder}
                  className="text-xl text-gray-900 bg-transparent border-b-2 border-primary-400 outline-none min-w-0 w-64 rounded-md focus:ring-0 focus:border-primary-400"
                />
              ) : (
                <div
                  className="flex items-center gap-1.5 cursor-pointer group"
                  onClick={() => setIsEditingName(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && setIsEditingName(true)}
                >
                  <h1 className="font-serif text-2xl text-gray-900">
                    {name || pageTitle}
                  </h1>
                  <Pencil
                    size={13}
                    className="text-gray-400 group-hover:text-gray-600 transition-all"
                  />
                </div>
              )}
            </>
          ) : (
            <h1 className="font-serif text-2xl text-gray-900">{pageTitle}</h1>
          )}
        </div>

        <div className="md:ml-auto flex items-center gap-3">
          {onEnabledChange && (
            <>
              <BBSwitch
                label="Enabled"
                enabled={isEnabled ?? true}
                setEnabled={onEnabledChange}
              />
              <div className="w-px h-5 bg-gray-200" />
            </>
          )}

          <BBSwitch
            label="Default"
            enabled={isDefault}
            setEnabled={onDefaultChange}
          />

          <div className="w-px h-5 bg-gray-200" />

          {!isCreate && (
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-500 font-medium transition-colors"
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}

          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onSubmit}
            disabled={!(isValid && dirty)}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isCreate ? "Create" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="mt-4 max-w-xs">
        <BrandAutocomplete
          brands={brands}
          selected={brandId}
          hideLabel
          onChange={onBrandChange}
        />
      </div>
    </div>
  )
}

export default ConfigEditHeader
